import type { CityConfig } from "@/pages/city-template/types";

export const lawrenceConfig: CityConfig = {
  city: "Lawrence",
  state: "Kansas",
  stateAbbr: "KS",
  slug: "lawrence",
  route: "/cities/lawrence",
  title: "Emergency Passport Lawrence, KS | Fast Passport Renewal & Expedited Services",
  metaDescription: "Emergency passport and expedited passport services in Lawrence, KS — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Lawrence and Douglas County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Lawrence, Kansas",
  heroSubtitle: "Passport expired before your study abroad trip from KU? Flying out of MCI in days? Fast Passport Center helps Lawrence travelers get their passports fast — as fast as 24 hours. A+ BBB rated. No office visit required for most services.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Lawrence%20Kansas%20downtown%20Massachusetts%20Street%20with%20historic%20brick%20buildings%20and%20vibrant%20college%20town%20atmosphere%2C%20University%20of%20Kansas%20campus%20in%20background%2C%20tree-lined%20streets%20with%20autumn%20colors%2C%20Jayhawk%20Boulevard%2C%20eclectic%20shops%20and%20cafes%2C%20warm%20afternoon%20sunlight%2C%20Midwestern%20university%20town%20charm%2C%20limestone%20architecture&width=1800&height=700&seq=lawrence-hero-01&orientation=landscape",
  heroImageAlt: "Lawrence Kansas downtown Massachusetts Street with historic brick buildings and university town atmosphere",
  geoPosition: "38.9717;-95.2352",
  geoPlacename: "Lawrence, Kansas",
  lastModified: "2026-06-07",
  regionalAgency: {
    name: "Arkansas Passport Center",
    address: "191 Office Park Dr, Hot Springs, AR 71913",
    distance: "About 340 miles south — 5 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Lawrence", zips: "66044–66049" },
    { name: "Topeka", zips: "66601–66699" },
    { name: "Eudora", zips: "66025" },
    { name: "Baldwin City", zips: "66006" },
    { name: "Tonganoxie", zips: "66086" },
    { name: "Perry", zips: "66073" },
    { name: "Lecompton", zips: "66050" },
    { name: "De Soto", zips: "66018–66019" },
    { name: "Olathe", zips: "66051–66063" },
    { name: "Overland Park", zips: "66204–66225" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from your dorm or home in Lawrence in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from Douglas County", desc: "FedEx and UPS locations throughout Lawrence offer overnight shipping to our processing center." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission — critical for first-time student passport applicants." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots — no public appointment hunting needed." },
  ],
  testimonials: [
    {
      name: "Emily R.",
      location: "Lawrence, KS",
      rating: 5,
      text: "KU student heading to a semester abroad in Spain. Realized my passport expired 3 weeks before departure. Fast Passport Center got it renewed in 7 days. Shipped from the FedEx on Iowa Street. Made my flight — thank you!",
      service: "Passport Renewal",
    },
    {
      name: "Professor James D.",
      location: "Lawrence, KS",
      rating: 5,
      text: "Got invited to present at a conference in Berlin on short notice. My passport had expired the month before. Fast Passport Center handled the renewal in 5 days. The process was incredibly smooth for someone who needed it fast.",
      service: "Emergency Passport",
    },
    {
      name: "Sarah and Tom W.",
      location: "Baldwin City, KS",
      rating: 5,
      text: "We needed a child passport for our daughter's first international trip to visit family in the UK. Fast Passport Center made the process clear and stress-free. Passport arrived in 10 days. Great experience for Douglas County families.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "lawrence-main-post",
      name: "Lawrence Main Post Office",
      type: "USPS Passport Acceptance",
      address: "645 Vermont St",
      city: "Lawrence",
      state: "KS",
      zip: "66044",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Downtown Lawrence",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance location in Lawrence. Appointment required — book online well in advance, especially during peak student travel seasons.",
      icon: "ri-mail-line",
    },
    {
      id: "douglas-county-clerk",
      name: "Douglas County Clerk's Office",
      type: "County Passport Acceptance",
      address: "1100 Massachusetts St",
      city: "Lawrence",
      state: "KS",
      zip: "66044",
      phone: "785-832-5224",
      hours: "Mon–Fri 8am–5pm (by appointment)",
      distance: "Downtown Lawrence — Douglas County Courthouse",
      website: "https://www.douglascountyks.org/",
      websiteLabel: "Douglas County",
      notes: "County clerk passport acceptance in downtown Lawrence. Often has shorter wait times than USPS.",
      icon: "ri-government-line",
    },
    {
      id: "ku-union",
      name: "KU Student Union — Passport Services",
      type: "University Passport Acceptance",
      address: "1301 Jayhawk Blvd, Kansas Union, Level 4",
      city: "Lawrence",
      state: "KS",
      zip: "66045",
      phone: "785-864-4700",
      hours: "Mon–Fri 10am–4pm (academic year, by appointment)",
      distance: "KU Campus — Kansas Union",
      website: "https://international.ku.edu/",
      websiteLabel: "KU International Affairs",
      notes: "Convenient on-campus option for KU students, faculty, and staff. Appointment required. Limited hours during breaks and summer.",
      icon: "ri-building-2-line",
    },
    {
      id: "eudora-post",
      name: "Eudora Post Office",
      type: "USPS Passport Acceptance",
      address: "720 Main St",
      city: "Eudora",
      state: "KS",
      zip: "66025",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Eudora — about 9 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Nearby option for eastern Douglas County residents.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Lawrence before my study abroad trip?",
      a: "Yes. Through Fast Passport Center, Lawrence travelers — including KU students heading abroad — can get an expedited passport in as little as 2–9 business days. Emergency processing is available in as fast as 24 hours. Don't wait until finals week to check your passport.",
    },
    {
      q: "How fast can I renew my passport as a KU student in Lawrence?",
      a: "KU students and Lawrence residents can renew passports through Fast Passport Center in as little as 3 business days. If your passport was issued at age 16 or older and within the last 15 years, you likely qualify for mail-in renewal. Start early — don't wait until your study abroad deadline.",
    },
    {
      q: "Does Lawrence or KU have a passport agency on campus?",
      a: "KU does offer passport acceptance services at the Kansas Union for students, faculty, and staff — but this is standard processing only (6–13 weeks). For expedited service, Fast Passport Center offers Lawrence travelers processing as fast as 24 hours.",
    },
    {
      q: "Can international students at KU get a U.S. passport in Lawrence?",
      a: "No — U.S. passports are only available to U.S. citizens. International students at KU should contact their home country's embassy or consulate for passport services. Fast Passport Center can only assist U.S. citizens with U.S. passport applications.",
    },
    {
      q: "How long does a standard passport take in Lawrence?",
      a: "Standard post office processing in Lawrence runs 6–13 weeks. For study abroad, spring break international travel, or summer trips, Fast Passport Center offers Lawrence travelers processing as fast as 24 hours.",
    },
    {
      q: "What airports do Lawrence passport holders typically fly from?",
      a: "Kansas City International Airport (MCI) is the primary airport for Lawrence travelers, about 50 miles northeast. Direct international flights available. Fast Passport Center can help you meet your deadline.",
    },
    {
      q: "Can I get a passport renewal in Lawrence without visiting an office?",
      a: "Yes. Most Lawrence adults renewing a passport can do so entirely by mail. Start your order online with Fast Passport Center and ship from any FedEx or UPS in Lawrence.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Lawrence", address: "2320 Iowa St, Lawrence, KS 66046", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Lawrence", address: "1740 Massachusetts St, Lawrence, KS 66044", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — Lawrence", address: "721 Wakarusa Dr, Lawrence, KS 66049", hours: "Mon–Fri 7 AM – 10 PM" },
    { name: "Walmart — Lawrence", address: "550 Congressional Dr, Lawrence, KS 66049", hours: "Daily 7 AM – 11 PM" },
    { name: "CVS — Topeka", address: "2121 SW Fairlawn Rd, Topeka, KS 66614", hours: "Daily 7 AM – 10 PM" },
  ],
  dmvLocations: [
    {
      name: "Lawrence Driver License Office",
      address: "1035 N 3rd St, Suite A, Lawrence, KS 66044",
      hours: "Mon–Fri 7 AM – 5:45 PM",
      note: "Douglas County DMV office. Driver licenses, REAL ID, ID cards.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "MCI",
      name: "Kansas City International Airport",
      distance: "Primary international airport — 50 miles northeast",
      address: "1 International Sq, Kansas City, MO 64153",
      note: "MCI is the closest major airport for Lawrence travelers with direct international flights. Popular with KU study abroad students.",
      flightUrl: "https://www.flykci.com/",
      flightLabel: "MCI Airport Website",
      featured: true,
    },
  ],
  services: [
    {
      icon: "ri-time-line", title: "Emergency Passport",
      description: "Flying from MCI in 24–72 hours? Our emergency courier service is designed for urgent travel from Lawrence.",
      bullets: ["As fast as 24 hours", "Reserved agency submission slots", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line", title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Lawrence and Douglas County travelers renew fast — popular with KU students and faculty.",
      bullets: ["Eligible for expedited processing", "Ship from any FedEx or UPS in Lawrence", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line", title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Lawrence travelers through Form DS-64 and the replacement process.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line", title: "Child Passport",
      description: "Child passport expedited service for Lawrence families — we provide clear instructions and checklists.",
      bullets: ["Both parents must typically consent", "Clear checklist provided", "Expedited options after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line", title: "New Passport",
      description: "First-time passport application assistance for Lawrence residents and KU students.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line", title: "Passport Courier Service",
      description: "Registered U.S. Department of State courier — we submit through reserved agency channels.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line", title: "Name Change & Other Services",
      description: "Passport name changes after marriage, damaged passport replacement, second passports.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Lawrence, Kansas travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for Lawrence, KS residents and KU students." },
    { name: "Child Passport", description: "Expedited child passport services for Lawrence families with minors under 16." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Until Finals Week", description: "KU students often wait until the last minute to check their passport status. Check your passport the day you commit to studying abroad — not the week before you leave." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Drugstore photos often get rejected. We provide exact specs to avoid rejection." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrect Forms", description: "One wrong date or missing signature and your application gets sent back." },
    { icon: "ri-calendar-close-line", title: "Assuming Standard Processing is Fast Enough", description: "Standard USPS processing in Lawrence runs 6–13 weeks. If your trip is sooner, use our courier service." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Across Documents", description: "Your passport name must match your airline ticket exactly — critical for study abroad students." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your travel date from Lawrence." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We send you exactly what you need — nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Lawrence", description: "Drop off at any FedEx or UPS in Lawrence." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through reserved passport agency slots." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Completed", description: "Your passport is completed and shipped back to you." },
  ],
  processingOptions: [
    {
      speed: "24", unit: "Hours*", label: "Regional Agency Processing",
      tag: "Emergency Only", tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required", "Life-or-death emergencies only"],
      highlight: false, emergency: true,
    },
    {
      speed: "2–9", unit: "Business Days", label: "Fast Passport Center",
      tag: "Most Popular", tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Document review included", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5", unit: "Weeks", label: "Fast Passport Center",
      tag: "Standard Expedited", tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Document review included"],
      highlight: false, smartSaver: true,
    },
    {
      speed: "1–2", unit: "Months", label: "Self-Service (Post Office)",
      tag: "Slowest", tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access", "In-person clerk appointment required", "No document review", "Not suitable if travel is within 10 weeks"],
      highlight: false, muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots at Arkansas Passport Center", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Hot Springs, AR (5 hours)", fpc: "Ship documents overnight from Lawrence" },
    { feature: "Processing Speed", agency: "Urgent travel only", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Topeka", route: "/cities/topeka" },
    { name: "Fast passport renewal in Olathe", route: "/cities/olathe" },
    { name: "Emergency passport help near Overland Park", route: "/cities/overland-park" },
    { name: "Fast passport renewal for Douglas County", route: "/states/kansas" },
  ],
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship your documents overnight from any FedEx or UPS location in Lawrence.",
  fpcOfficeDistance: "Serving Lawrence and all of Douglas County remotely",
  serviceAreaIntro: "Whether you are a KU student, faculty member, or Lawrence resident, Fast Passport Center makes expedited passport processing simple and fast.",
  nearbyCitiesIntro: "We serve all Lawrence and Douglas County zip codes. Ship overnight and your documents are at our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Lawrence as simple as possible — perfect for busy students and professionals.",
  mistakesIntro: "These mistakes cause the most delays for Lawrence travelers. We review your documents before submission.",
  testimonialsIntro: "Real reviews from Lawrence and Douglas County travelers — including KU students — who trusted Fast Passport Center.",
  faqIntro: "The most common questions Lawrence travelers — especially KU students — ask about emergency and expedited passport services.",
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { label: "Birth & Death Certificates (Kansas Vital Statistics)", url: "https://www.kdhe.ks.gov/359/Vital-Statistics", desc: "Kansas Department of Health & Environment — Vital Statistics for birth and death certificates statewide" },
    { label: "Marriage Licenses (Douglas County Clerk)", url: "https://www.douglascountyks.org/", desc: "Douglas County Clerk for Lawrence and Douglas County" },
    { label: "Divorce Decrees (Douglas County District Court)", url: "https://www.douglascountyks.org/courts", desc: "Douglas County District Court for divorce decree records" },
    { label: "VitalChek Online Ordering", url: "https://www.vitalchek.com/", desc: "Order Kansas vital records online via VitalChek" },
  ],
  statePageRoute: "/states/kansas",
  statePageName: "Kansas",
};